import { useEffect } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import styled, { StyleSheetManager, ThemeProvider } from 'styled-components';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import { T as ThemeConsumer } from './index.logic2.js';
import combineThemes from '../combine-themes.js';
import { jsx } from 'react/jsx-runtime';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var ChildrenContainer$1 = function ChildrenContainer(_ref) {
  var className = _ref.className,
      children = _ref.children,
      stylesTarget = _ref.stylesTarget;
  return /*#__PURE__*/jsx(StyleSheetManager, {
    target: stylesTarget,
    children: /*#__PURE__*/jsx("div", {
      className: className,
      children: children
    })
  });
};

ChildrenContainer$1.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  stylesTarget: PropTypes.oneOfType([PropTypes.instanceOf(HTMLElement), PropTypes.instanceOf(ShadowRoot)])
};
ChildrenContainer$1.defaultProps = {
  className: '',
  children: null,
  stylesTarget: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var ChildrenContainer = styled(ChildrenContainer$1).withConfig({
  componentId: "gz-theme-manager-1671096099929__sc-s4l50s-0"
})(["", ""], function (_ref) {
  var styles = _ref.styles;
  return styles;
});

var _excluded = ["className", "base", "injectFirst", "styles", "icons", "breakpoints", "inheritStyles", "stylesTarget", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var ThemeManager = function ThemeManager(_ref) {
  var className = _ref.className,
      base = _ref.base,
      injectFirst = _ref.injectFirst,
      styles = _ref.styles,
      icons = _ref.icons,
      breakpoints = _ref.breakpoints,
      inheritStyles = _ref.inheritStyles,
      stylesTarget = _ref.stylesTarget,
      _children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var dataAttributes = useDataAttributes(rest);
  useEffect(function () {
    // If we have the injectFirst prop, we have to re-order the styles.
    if (injectFirst) {
      // Get the DOM element that contains the styles.
      var target = stylesTarget || document.head; // Get the styled-components style tag

      var scStyles = target.querySelector('[data-styled="active"]'); // Get and parse all the children of the target

      var allChildren = Array.from(target.children); // Get the first styles tag, it could be a <style> or a <link rel="stylesheet">

      var firstStylesTag = allChildren.find(function (each) {
        var tagName = each.tagName.toLowerCase();
        return tagName === 'style' && !each.dataset.styled || tagName === 'link' && each.rel === 'stylesheet';
      }); // In case that exist a styles tag, we move the styled-components style tag.

      if (firstStylesTag) {
        target.insertBefore(scStyles, firstStylesTag);
      }
    }
  }, [injectFirst, stylesTarget]);

  if (styles || icons || base || breakpoints) {
    return /*#__PURE__*/jsx(ThemeConsumer, {
      children: function children(theme) {
        var finalTheme = {
          styles: styles,
          icons: icons,
          base: base,
          breakpoints: breakpoints
        }; // Only for nested ThemeManager's

        if (theme && inheritStyles) {
          finalTheme = combineThemes({
            styles: [theme.styles, styles],
            icons: [theme.icons, icons],
            base: [theme.base, base],
            breakpoints: [theme.breakpoints, breakpoints]
          });
        }

        return /*#__PURE__*/jsx(ThemeProvider, {
          theme: finalTheme,
          children: /*#__PURE__*/jsx(ChildrenContainer, _objectSpread(_objectSpread({
            className: "gz-theme-manager ".concat(className),
            styles: base,
            stylesTarget: stylesTarget // eslint-disable-next-line react/jsx-props-no-spreading

          }, dataAttributes), {}, {
            children: _children
          }))
        });
      }
    });
  }

  return /*#__PURE__*/jsx(ChildrenContainer, _objectSpread(_objectSpread({
    className: "gz-theme-manager ".concat(className),
    stylesTarget: stylesTarget // eslint-disable-next-line react/jsx-props-no-spreading

  }, dataAttributes), {}, {
    children: _children
  }));
};

ThemeManager.COMPONENT_ID = 'Gearz.ThemeManager';
ThemeManager.displayName = ThemeManager.COMPONENT_ID;
ThemeManager.propTypes = {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the theme manager.').isRequired,
  base: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)) // needed for css method of styled-components
  ], 'Base styles of the theme.'),
  injectFirst: PropTypes.boolWithDescription('Flag to force the injection of styles in the first place.'),
  styles: PropTypes.shapeWithDescription({}, 'Object of styles of the components.'),
  icons: PropTypes.shapeWithDescription({}, 'Object of icons.'),
  breakpoints: PropTypes.shapeWithDescription({
    NAME_OF_BREAKPOINT: PropTypes.shapeWithDescription({
      suffix: PropTypes.stringWithDescription('String used to identify the breakpoint. It will be used to generate the props by breakpoint.'),
      columns: PropTypes.numberWithDescription('Number of columns to be configured in the grid.'),
      rowGap: PropTypes.numberWithDescription('Separation (in pixels) of each row of the grid.'),
      columnGap: PropTypes.numberWithDescription('Separation (in pixels) of each column of the grid.'),
      dimensions: PropTypes.shapeWithDescription({
        from: PropTypes.number,
        to: PropTypes.number
      }, 'Object with from (minWidth) and to (maxWidth) dimensions (in pixels) of the breakpoint.')
    }, 'Breakpoint')
  }, 'Breakpoints defined by the theme.'),
  stylesTarget: PropTypes.oneOfTypeWithDescription([PropTypes.instanceOf(HTMLElement), PropTypes.instanceOf(ShadowRoot)], 'Element where the styles will be injected. By default, styles will be injected in `document.head`.'),
  inheritStyles: PropTypes.boolWithDescription('Flag to inherit the styles from the parent theme manager.')
};
ThemeManager.defaultProps = {
  className: '',
  base: '',
  injectFirst: false,
  styles: {},
  icons: {},
  breakpoints: {},
  stylesTarget: null,
  inheritStyles: true
};

export { ThemeManager as T };
